import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Workflow, Bot as BotIcon, Pause, Play, Square, Trash2, Loader2, User, AlertTriangle, Infinity as InfinityIcon } from "lucide-react";
import { toast } from "sonner";
import { bots as botsApi } from "../lib/api";
import MessageBubble from "../components/MessageBubble";
import ConfirmDialog from "../components/ConfirmDialog";
import { BOT_DOT, RUN_STATUS } from "./BotsPage";

/**
 * Visualização de execução do pipeline (estilo n8n):
 * nós animados mostrando qual bot está trabalhando + feed em tempo real.
 */
export default function BotRunPage() {
    const { runId } = useParams();
    const navigate = useNavigate();
    const [run, setRun] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [confirmDel, setConfirmDel] = useState(false);
    const [busy, setBusy] = useState("");
    const feedEndRef = useRef(null);
    const msgCountRef = useRef(0);

    const load = useCallback(async () => {
        try {
            const data = await botsApi.getRun(runId);
            setRun(data);
        } catch (e) {
            if (e?.response?.status === 404) setNotFound(true);
        }
    }, [runId]);

    // Poll em tempo real: 2.5s rodando, 8s parado
    useEffect(() => {
        load();
        let timer;
        const tick = async () => {
            await load();
            const fast = true; // sempre atualiza; intervalo decide abaixo
            void fast;
        };
        timer = setInterval(tick, 2500);
        return () => clearInterval(timer);
    }, [load]);

    // Auto-scroll quando chegam mensagens novas
    // Rola para o fim APENAS na primeira carga — nunca durante a leitura.
    useEffect(() => {
        const count = run?.messages?.length || 0;
        if (count > 0 && !msgCountRef.current) {
            msgCountRef.current = count;
            feedEndRef.current?.scrollIntoView({ behavior: "auto", block: "end" });
        }
    }, [run?.messages?.length]);

    const action = async (kind) => {
        setBusy(kind);
        try {
            if (kind === "pause") setRun(await botsApi.pauseRun(runId));
            if (kind === "resume") setRun(await botsApi.resumeRun(runId));
            if (kind === "stop") setRun(await botsApi.stopRun(runId));
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Ação não permitida agora.");
        } finally {
            setBusy("");
            load();
        }
    };

    const doDelete = async () => {
        try {
            await botsApi.removeRun(runId);
            toast.success("Execução excluída.");
            navigate("/bots");
        } catch {
            toast.error("Não foi possível excluir.");
            setConfirmDel(false);
        }
    };

    if (notFound) {
        return (
            <div className="min-h-[100dvh] bg-background flex flex-col items-center justify-center gap-3 text-muted-foreground">
                <Workflow className="w-8 h-8 opacity-50" />
                <div className="text-sm">Execução não encontrada.</div>
                <button onClick={() => navigate("/bots")} className="text-primary text-sm font-semibold">Voltar aos bots</button>
            </div>
        );
    }
    if (!run) {
        return (
            <div className="min-h-[100dvh] bg-background flex items-center justify-center text-muted-foreground">
                <Loader2 className="w-6 h-6 animate-spin" />
            </div>
        );
    }

    const st = RUN_STATUS[run.status] || RUN_STATUS.stopped;
    const running = run.status === "running";
    const activeStep = run.active_step ?? -1;

    return (
        <div className="h-[100dvh] bg-background flex flex-col">
            {/* Header */}
            <header className="shrink-0 h-12 md:h-14 border-b border-border bg-[#0A0A0A]/95 backdrop-blur-xl flex items-center gap-2 px-3 sm:px-6" data-testid="run-header">
                <button type="button" onClick={() => navigate(run?.group_id ? `/bots/g/${run.group_id}` : "/bots")} className="p-2 -ml-2 rounded-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors" aria-label="Voltar" data-testid="run-back">
                    <ArrowLeft className="w-5 h-5" />
                </button>
                <Workflow className="w-4 h-4 text-primary shrink-0" />
                <h1 className="font-display font-bold tracking-tight text-sm truncate">{run.title || "Pipeline"}</h1>
                <div className="flex-1" />
                <div className="flex items-center gap-1">
                    {running && (
                        <button type="button" onClick={() => action("pause")} disabled={!!busy} data-testid="run-pause-btn" title="Pausar (termina o passo atual)"
                            className="p-2 rounded-sm text-amber-400 hover:bg-amber-500/10 transition-colors disabled:opacity-50" aria-label="Pausar">
                            {busy === "pause" ? <Loader2 className="w-4.5 h-4.5 animate-spin" /> : <Pause className="w-4.5 h-4.5" />}
                        </button>
                    )}
                    {(run.status === "paused" || run.status === "error") && (
                        <button type="button" onClick={() => action("resume")} disabled={!!busy} data-testid="run-resume-btn" title="Continuar"
                            className="p-2 rounded-sm text-emerald-400 hover:bg-emerald-500/10 transition-colors disabled:opacity-50" aria-label="Continuar">
                            {busy === "resume" ? <Loader2 className="w-4.5 h-4.5 animate-spin" /> : <Play className="w-4.5 h-4.5" />}
                        </button>
                    )}
                    {(running || run.status === "paused" || run.status === "error") && (
                        <button type="button" onClick={() => action("stop")} disabled={!!busy} data-testid="run-stop-btn" title="Parar (imediato)"
                            className="p-2 rounded-sm text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-50" aria-label="Parar">
                            {busy === "stop" ? <Loader2 className="w-4.5 h-4.5 animate-spin" /> : <Square className="w-4.5 h-4.5" />}
                        </button>
                    )}
                    <span className={`shrink-0 text-[9px] md:text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full border ${st.cls}`} data-testid="run-status-badge">{st.label}</span>
                    <button type="button" onClick={() => setConfirmDel(true)} data-testid="run-delete-btn" title="Excluir execução"
                        className="p-2 rounded-sm text-muted-foreground hover:text-red-400 hover:bg-red-500/10 transition-colors" aria-label="Excluir">
                        <Trash2 className="w-4.5 h-4.5" />
                    </button>
                </div>
            </header>

            {/* Pipeline (nós estilo n8n) — apenas scroll horizontal */}
            <div className="shrink-0 border-b border-border bg-[#0b0b0b] px-3 sm:px-6 py-3" data-testid="pipeline-viz">
                <div className="flex items-center gap-0 overflow-x-auto overflow-y-hidden">
                    {(run.steps || []).map((s, i) => {
                        const isActive = activeStep === i && running;
                        // Nós já executados neste ciclo (antes do ativo / do próximo passo)
                        const doneIdx = activeStep >= 0 ? activeStep : (run.next_step ?? 0);
                        const isDone = i < doneIdx || run.status === "done" || run.cycle > 1;
                        const dot = BOT_DOT[s.color || ""];
                        return (
                            <React.Fragment key={`${s.bot_id}-${i}`}>
                                {i > 0 && (
                                    <div className={`shrink-0 w-9 md:w-14 h-1.5 relative overflow-hidden rounded-full ${activeStep === i && running ? "bg-primary/20" : isDone || (activeStep > i && running) ? "bg-primary/50" : "bg-border"}`}>
                                        {activeStep === i && running && (
                                            <span className="absolute inset-y-0 left-0 bg-primary rounded-full pipe-grow" />
                                        )}
                                    </div>
                                )}
                                <div className="shrink-0 flex flex-col items-center gap-1 px-1 py-1" data-testid={`pipeline-node-${i}`}>
                                    <div className={`relative w-11 h-11 md:w-12 md:h-12 rounded-full border flex items-center justify-center transition-all ${
                                        isActive ? "border-primary bg-primary/15 node-active" : isDone ? "border-primary/50 bg-primary/[0.06]" : "border-border bg-[#141414]"
                                    }`}>
                                        <BotIcon className={`w-5 h-5 ${isActive ? "text-primary" : isDone ? "text-primary/70" : "text-muted-foreground"}`} />
                                        <span className={`absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-0.5 rounded-full border-2 border-[#0b0b0b] ${dot} text-[9px] font-bold text-black/80 flex items-center justify-center leading-none`}>{i + 1}</span>
                                    </div>
                                    <span className={`text-[9px] md:text-[10px] font-semibold max-w-[76px] truncate ${isActive ? "text-primary" : "text-muted-foreground"}`}>{s.name}</span>
                                </div>
                            </React.Fragment>
                        );
                    })}
                    <div className="shrink-0 ml-auto pl-4 text-right">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-1 justify-end">
                            Ciclo {run.cycle}{run.max_cycles > 0 ? ` / ${run.max_cycles}` : <InfinityIcon className="w-3 h-3" />}
                        </div>
                    </div>
                </div>
            </div>

            {/* Feed */}
            <main className="flex-1 overflow-y-auto">
                <div className="max-w-3xl mx-auto w-full px-3 sm:px-6 py-4 space-y-4">
                    {(run.messages || []).map((m) => <RunMessage key={m.id} msg={m} steps={run.steps} />)}
                    {running && activeStep >= 0 && (
                        <div className="fade-in py-2" data-testid="run-typing">
                            <span className="text-shimmer text-sm font-medium">{run.steps?.[activeStep]?.name} está trabalhando…</span>
                        </div>
                    )}
                    <div ref={feedEndRef} />
                </div>
            </main>

            <ConfirmDialog open={confirmDel} title="Excluir execução?" description="Todo o histórico desse pipeline será apagado." confirmLabel="Excluir" destructive onConfirm={doDelete} onCancel={() => setConfirmDel(false)} testId="run-delete-confirm" />
        </div>
    );
}

function RunMessage({ msg, steps }) {
    if (msg.role === "input") {
        return (
            <div className="flex flex-col items-end gap-1" data-testid="run-msg-input">
                <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                    <User className="w-3 h-3" /> Entrada inicial
                </div>
                <div className="max-w-[92%] md:max-w-[80%] rounded-2xl rounded-tr-sm bg-primary/15 border border-primary/25 px-4 py-3 text-sm whitespace-pre-wrap">{msg.content}</div>
            </div>
        );
    }

    if (msg.role === "system") {
        return (
            <div className="flex items-start gap-2.5 rounded-md border border-amber-500/30 bg-amber-500/10 px-4 py-3" data-testid="run-msg-system">
                <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                <div className="text-xs text-amber-200/90 leading-relaxed">{msg.content}</div>
            </div>
        );
    }

    const stepColor = BOT_DOT[steps?.[msg.step_index]?.color || ""];
    return (
        <div className="flex flex-col" data-testid="run-msg-bot">
            <div className="flex items-center gap-2 mb-1.5">
                <span className={`w-5 h-5 rounded-full ${stepColor} flex items-center justify-center shrink-0`}>
                    <BotIcon className="w-3 h-3 text-black/70" />
                </span>
                <span className="text-xs font-semibold">{msg.bot_name}</span>
                <span className="text-[10px] font-mono text-muted-foreground">ciclo {msg.cycle} · passo {(msg.step_index ?? 0) + 1}</span>
            </div>
            <MessageBubble msg={{ role: "assistant", content: msg.content || "", attachments: msg.attachments || [] }} />
        </div>
    );
}
